/**
 * Inline payment-product form for the registration Payments pane — the
 * replacement for the retired Pricing page's create/edit modals. One component,
 * both variants (fee / donation) and modes (create / edit).
 *
 * Money units: BOTH endpoints take CENTS everywhere (unified 2026-08-26 —
 * create used to take dollars for the base amount). The payload builders below
 * are the only place dollar-text → cents conversion lives. Create omits unset
 * tier fields; update sends null to clear them ($unset server-side).
 *
 * Create mode shows no name input — the caller passes an auto-generated
 * human-readable `autoName` (the name is pushed to Stripe as "<Org> — <name>"
 * and shows on checkout pages and receipts, so it must stay presentable).
 */
import { useState } from 'react';
import { toast } from 'sonner';
import { ChevronDown, ChevronRight, TriangleAlert } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';

export interface UsageRef {
  type: string;
  _id: string;
  name?: string;
}

export interface ProductUsage {
  usedBy: UsageRef[];
  total: number;
}

export interface ProductLike {
  _id?: string;
  name?: string;
  description?: string;
  amount?: number; // cents
  perTeam?: boolean;
  perPlayer?: boolean;
  playerPaySeparately?: boolean;
  installments?: number | null;
  additionalTeamAmount?: number | null;
  additionalTeamThreshold?: number | null;
  earlyBirdAmount?: number | null;
  earlyBirdDeadline?: string | Date | null;
  lateRegistrationAmount?: number | null;
  lateRegistrationStartDate?: string | Date | null;
}

export type ProductPayload = Record<string, unknown>;

interface InlineProductFormProps {
  variant: 'fee' | 'donation';
  mode: 'create' | 'edit';
  initialProduct?: ProductLike;
  autoName?: string;
  usage?: ProductUsage | null;
  saving?: boolean;
  onSubmit: (payload: ProductPayload) => void;
  onCancel: () => void;
}

const toDateInputValue = (value?: string | Date | null): string => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
};

const centsToText = (c?: number | null): string => (c != null ? String(c / 100) : '');

const textToCents = (raw: string): number | null => {
  const dollars = Number.parseFloat(raw);
  return Number.isFinite(dollars) ? Math.round(dollars * 100) : null;
};

const USAGE_DISPLAY_CAP = 5;

export function ProductUsageCaution({ usage, variant }: { usage: ProductUsage; variant: 'fee' | 'donation' }) {
  const shown = usage.usedBy.slice(0, USAGE_DISPLAY_CAP);
  const overflow = usage.total - shown.length;
  return (
    <div className="rounded-lg border border-warning/30 bg-warning/10 p-3" data-testid="product-usage-caution">
      <div className="flex items-center gap-2 text-sm font-medium text-warning">
        <TriangleAlert className="h-4 w-4" />
        This {variant === 'fee' ? 'fee' : 'donation option'} is used by {usage.total} other{' '}
        {usage.total === 1 ? 'competition' : 'competitions'}. Saving changes it for all of them.
      </div>
      <ul className="mt-1 list-disc pl-9 text-xs text-warning">
        {shown.map((u) => (
          <li key={u._id}>{u.name || u._id}</li>
        ))}
        {overflow > 0 && <li>+{overflow} more</li>}
      </ul>
    </div>
  );
}

export default function InlineProductForm({
  variant, mode, initialProduct, autoName, usage, saving, onSubmit, onCancel,
}: InlineProductFormProps) {
  const p = initialProduct || {};
  const [name, setName] = useState<string>(p.name || '');
  const [description, setDescription] = useState<string>(p.description || '');
  const [pricingType, setPricingType] = useState<'perTeam' | 'perPlayer'>(p.perPlayer ? 'perPlayer' : 'perTeam');
  const [playerPaySeparately, setPlayerPaySeparately] = useState<boolean>(!!p.playerPaySeparately);
  // Money fields are TEXT inputs (CLAUDE.md: never type="number" — a stray
  // scroll over a spinner silently rewrites a price).
  const [moneyText, setMoneyText] = useState<Record<string, string>>({
    amount: centsToText(p.amount),
    earlyBirdAmount: centsToText(p.earlyBirdAmount),
    lateRegistrationAmount: centsToText(p.lateRegistrationAmount),
    additionalTeamAmount: centsToText(p.additionalTeamAmount),
  });
  const [earlyBirdDeadline, setEarlyBirdDeadline] = useState<string>(toDateInputValue(p.earlyBirdDeadline));
  const [lateRegistrationStartDate, setLateRegistrationStartDate] = useState<string>(toDateInputValue(p.lateRegistrationStartDate));
  const [thresholdText, setThresholdText] = useState<string>(
    p.additionalTeamThreshold != null ? String(p.additionalTeamThreshold) : '',
  );
  // INT-0018: installment plans — '' = off, otherwise '2' | '3' | '4'.
  const [installmentsText, setInstallmentsText] = useState<string>(
    p.installments != null ? String(p.installments) : '',
  );
  const [advancedOpen, setAdvancedOpen] = useState<boolean>(
    p.additionalTeamAmount != null
    || p.installments != null
    || p.earlyBirdAmount != null
    || p.earlyBirdDeadline != null
    || p.lateRegistrationAmount != null
    || p.lateRegistrationStartDate != null,
  );

  const onMoneyChange = (field: string, raw: string) => {
    setMoneyText((m) => ({ ...m, [field]: raw.replace(/[^0-9.]/g, '') }));
  };

  const validate = (): boolean => {
    const early = textToCents(moneyText.earlyBirdAmount);
    const late = textToCents(moneyText.lateRegistrationAmount);
    // Both halves of each time-based pricing pair are required, or neither.
    if ((early != null) !== (earlyBirdDeadline !== '')) {
      toast.error('Early-bird pricing needs both a price and a deadline.');
      return false;
    }
    if ((late != null) !== (lateRegistrationStartDate !== '')) {
      toast.error('Late registration pricing needs both a price and a start date.');
      return false;
    }
    if (earlyBirdDeadline !== '' && lateRegistrationStartDate !== ''
        && new Date(earlyBirdDeadline) > new Date(lateRegistrationStartDate)) {
      toast.error('Early-bird deadline must be on or before the late registration start date.');
      return false;
    }
    return true;
  };

  // CENTS everywhere (matching the update endpoint since 2026-08-26). Unset
  // advanced fields are omitted entirely.
  const buildCreatePayload = (): ProductPayload => {
    const payload: ProductPayload = {
      amount: textToCents(moneyText.amount) ?? 0,
      perTeam: pricingType === 'perTeam',
      perPlayer: pricingType === 'perPlayer',
      playerPaySeparately: pricingType === 'perPlayer' && playerPaySeparately,
      isFree: false,
    };
    const early = textToCents(moneyText.earlyBirdAmount);
    const late = textToCents(moneyText.lateRegistrationAmount);
    const additional = textToCents(moneyText.additionalTeamAmount);
    if (early != null) payload.earlyBirdAmount = early;
    if (earlyBirdDeadline !== '') payload.earlyBirdDeadline = new Date(earlyBirdDeadline);
    if (late != null) payload.lateRegistrationAmount = late;
    if (lateRegistrationStartDate !== '') payload.lateRegistrationStartDate = new Date(lateRegistrationStartDate);
    if (additional != null) payload.additionalTeamAmount = additional;
    if (thresholdText !== '') payload.additionalTeamThreshold = Math.max(1, parseInt(thresholdText, 10) || 1);
    if (installmentsText !== '') payload.installments = parseInt(installmentsText, 10);
    return payload;
  };

  // UPDATE endpoint convention: everything in CENTS; null explicitly clears an
  // optional tier ($unset server-side), undefined leaves it alone.
  const buildUpdatePayload = (): ProductPayload => ({
    name,
    amount: textToCents(moneyText.amount) ?? 0,
    perTeam: pricingType === 'perTeam',
    perPlayer: pricingType === 'perPlayer',
    playerPaySeparately: pricingType === 'perPlayer' && playerPaySeparately,
    isFree: false,
    earlyBirdAmount: textToCents(moneyText.earlyBirdAmount),
    earlyBirdDeadline: earlyBirdDeadline === '' ? null : new Date(earlyBirdDeadline),
    lateRegistrationAmount: textToCents(moneyText.lateRegistrationAmount),
    lateRegistrationStartDate: lateRegistrationStartDate === '' ? null : new Date(lateRegistrationStartDate),
    additionalTeamAmount: textToCents(moneyText.additionalTeamAmount),
    additionalTeamThreshold: thresholdText === '' ? null : Math.max(1, parseInt(thresholdText, 10) || 1),
    installments: installmentsText === '' ? null : parseInt(installmentsText, 10),
  });

  const handleSubmit = () => {
    if (variant === 'donation') {
      onSubmit(mode === 'create' ? { description } : { name, description });
      return;
    }
    if (!validate()) return;
    onSubmit(mode === 'create' ? buildCreatePayload() : buildUpdatePayload());
  };

  const idPrefix = `${variant}-${mode}`;

  return (
    <div className="space-y-4 rounded-lg border bg-muted/30 p-4" data-testid="inline-product-form">
      <div className="text-sm font-medium">
        {mode === 'create'
          ? (variant === 'fee' ? 'New registration fee' : 'New donation option')
          : (variant === 'fee' ? 'Edit registration fee' : 'Edit donation option')}
      </div>

      {mode === 'create' && autoName && (
        <p className="text-xs text-muted-foreground">
          Will appear on the checkout page and receipts as <span className="font-medium">“{autoName}”</span>.
        </p>
      )}

      {mode === 'edit' && usage && usage.total > 0 && <ProductUsageCaution usage={usage} variant={variant} />}

      {mode === 'edit' && (
        <div className="space-y-1">
          <Label htmlFor={`${idPrefix}-name`}>Name</Label>
          <Input id={`${idPrefix}-name`} value={name} onChange={(e) => setName(e.target.value)} />
          <p className="text-xs text-muted-foreground">Shown on the checkout page and receipts.</p>
        </div>
      )}

      {variant === 'donation' ? (
        <div className="space-y-1">
          <Label htmlFor={`${idPrefix}-description`}>Description</Label>
          <Textarea
            id={`${idPrefix}-description`}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Support our league"
          />
          <p className="text-xs text-muted-foreground">Teams choose their own donation amount during registration.</p>
        </div>
      ) : (
        <>
          <div className="space-y-1">
            <Label htmlFor={`${idPrefix}-amount`}>Amount ($)</Label>
            <Input
              id={`${idPrefix}-amount`}
              type="text"
              inputMode="decimal"
              placeholder="e.g. 100.00"
              value={moneyText.amount}
              onChange={(e) => onMoneyChange('amount', e.target.value)}
            />
          </div>

          <div className="space-y-1">
            <Label>Pricing Type</Label>
            <RadioGroup
              value={pricingType}
              onValueChange={(v) => {
                setPricingType(v as 'perTeam' | 'perPlayer');
                if (v === 'perTeam') setPlayerPaySeparately(false);
              }}
              className="flex space-x-4"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem id={`${idPrefix}-perTeam`} value="perTeam" />
                <Label htmlFor={`${idPrefix}-perTeam`}>Per Team</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem id={`${idPrefix}-perPlayer`} value="perPlayer" />
                <Label htmlFor={`${idPrefix}-perPlayer`}>Per Player</Label>
              </div>
            </RadioGroup>
          </div>

          {pricingType === 'perPlayer' && (
            <div className="flex items-center space-x-2">
              <Switch
                id={`${idPrefix}-playerPaySeparately`}
                checked={playerPaySeparately}
                onCheckedChange={setPlayerPaySeparately}
              />
              <Label htmlFor={`${idPrefix}-playerPaySeparately`} className="text-sm text-muted-foreground">
                Players pay separately
              </Label>
            </div>
          )}

          <Collapsible open={advancedOpen} onOpenChange={setAdvancedOpen} className="border-t pt-3">
            <CollapsibleTrigger asChild>
              <Button type="button" variant="ghost" className="w-full justify-start px-0 hover:bg-transparent">
                {advancedOpen ? <ChevronDown className="mr-2 h-4 w-4" /> : <ChevronRight className="mr-2 h-4 w-4" />}
                Advanced pricing
                <span className="ml-2 text-xs font-normal text-muted-foreground">Optional (leave blank to disable)</span>
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="space-y-3 pt-2">
              <div className="space-y-1">
                <Label htmlFor={`${idPrefix}-earlyBirdAmount`}>Early-bird Price ($)</Label>
                <Input
                  id={`${idPrefix}-earlyBirdAmount`}
                  type="text"
                  inputMode="decimal"
                  placeholder="e.g. 80.00"
                  value={moneyText.earlyBirdAmount}
                  onChange={(e) => onMoneyChange('earlyBirdAmount', e.target.value)}
                />
                <p className="text-xs text-muted-foreground">Total price charged before the deadline. Replaces the base Amount.</p>
              </div>
              <div className="space-y-1">
                <Label htmlFor={`${idPrefix}-earlyBirdDeadline`}>Early-bird Deadline</Label>
                <Input
                  id={`${idPrefix}-earlyBirdDeadline`}
                  type="date"
                  value={earlyBirdDeadline}
                  onChange={(e) => setEarlyBirdDeadline(e.target.value)}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor={`${idPrefix}-lateRegistrationAmount`}>Late Registration Price ($)</Label>
                <Input
                  id={`${idPrefix}-lateRegistrationAmount`}
                  type="text"
                  inputMode="decimal"
                  placeholder="e.g. 120.00"
                  value={moneyText.lateRegistrationAmount}
                  onChange={(e) => onMoneyChange('lateRegistrationAmount', e.target.value)}
                />
                <p className="text-xs text-muted-foreground">Total price charged on/after the start date. Replaces the base Amount.</p>
              </div>
              <div className="space-y-1">
                <Label htmlFor={`${idPrefix}-lateRegistrationStartDate`}>Late Registration Start</Label>
                <Input
                  id={`${idPrefix}-lateRegistrationStartDate`}
                  type="date"
                  value={lateRegistrationStartDate}
                  onChange={(e) => setLateRegistrationStartDate(e.target.value)}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor={`${idPrefix}-additionalTeamAmount`}>Additional Team Price ($)</Label>
                <Input
                  id={`${idPrefix}-additionalTeamAmount`}
                  type="text"
                  inputMode="decimal"
                  placeholder="e.g. 25.00"
                  value={moneyText.additionalTeamAmount}
                  onChange={(e) => onMoneyChange('additionalTeamAmount', e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  Total price charged for the user&apos;s 2nd+ team. Replaces the base Amount, NOT a discount off it.
                </p>
              </div>
              <div className="space-y-1">
                <Label htmlFor={`${idPrefix}-additionalTeamThreshold`}>Kicks in after</Label>
                <div className="flex items-center gap-2">
                  <Input
                    id={`${idPrefix}-additionalTeamThreshold`}
                    type="text"
                    inputMode="numeric"
                    value={thresholdText}
                    onChange={(e) => setThresholdText(e.target.value.replace(/[^0-9]/g, ''))}
                    className="w-24"
                  />
                  <span className="text-sm text-muted-foreground">prior paid team(s)</span>
                </div>
              </div>
              <div className="space-y-1">
                <Label htmlFor={`${idPrefix}-installments`}>Payment plan (installments)</Label>
                <select
                  id={`${idPrefix}-installments`}
                  className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm"
                  value={installmentsText}
                  onChange={(e) => setInstallmentsText(e.target.value)}
                >
                  <option value="">Off (pay in full only)</option>
                  <option value="2">2 monthly payments</option>
                  <option value="3">3 monthly payments</option>
                  <option value="4">4 monthly payments</option>
                </select>
                <p className="text-xs text-muted-foreground">
                  Registrants can choose to split this fee into monthly card payments
                  (first at checkout, rest auto-charged). Each extra installment adds
                  60¢ processing, shown to the registrant upfront.
                </p>
              </div>
            </CollapsibleContent>
          </Collapsible>
        </>
      )}

      <div className="flex justify-end gap-2">
        <Button type="button" variant="secondary" onClick={onCancel} disabled={!!saving}>
          Cancel
        </Button>
        <Button type="button" onClick={handleSubmit} disabled={!!saving}>
          {mode === 'create'
            ? (variant === 'fee' ? 'Create fee' : 'Create donation option')
            : 'Save changes'}
        </Button>
      </div>
    </div>
  );
}
